import { type ILandlordOnboardInvite } from 'src/api/onboard-api';
import type { UserSettings } from 'src/services/api/users/settings/types';

export interface IIngestEmail {
    individual: string;
    /**
     * This is only set for new users
     * Existing (legacy) users will not have this, at least initially
     */
    team?: string;
    in_use: { individual: boolean; team: boolean; } | null;
}

export interface ILeadContact {
    sms_phone_number: string | null;
    email: { alias: string; domain: string } | null;
}

export interface INotificationPreferences {
    is_notify_on_new_messages: boolean;
    // one of "sms" or "email" when not null
    notify_method_on_new_messages: string | null;
}

export enum LeasingTeamRole {
    COMPANY_ADMIN = 'company_admin',
    FRONT_DESK = 'front_desk',
    REGIONAL_MANAGER = 'regional_manager',
    PROPERTY_MANAGER = 'property_manager',
}

/**
 * Presence (shift-support feature). A user is "away" either manually (during
 * their working hours) or because they are outside their configured working hours.
 */
export enum EPresence {
    ONLINE = 'online',
    AWAY = 'away',
}

/** How long a manual presence override lasts. */
export enum EPresenceDuration {
    REST_OF_DAY = 'rest_of_day',
    ONE_HOUR = 'one_hour',
}

/** Why a user is online/away — lets the UI decide whether "Set to Online" applies. */
export enum EPresenceSource {
    /** away because the user manually set themselves away during their working hours */
    OVERRIDE = 'override',
    /** derived from the user's configured working hours (online inside, away outside) */
    WORKING_HOURS = 'working_hours',
    /** online because no working hours are configured (and no override) */
    DEFAULT = 'default',
}

/** Nested presence object returned by the API (GET /me, GET /api/landlord/team/members). */
export interface IPresenceInfo {
    value: EPresence;
    /** Unix timestamp (seconds) at which the current presence is expected to change, or null. */
    exp: number | null;
    source: EPresenceSource;
}

/**
 * Minimal information about other users
 */
export interface IOtherUser {
    id: number;
    uuid: string;
    // guaranteed to be set
    name: string;
    leasing_team_id: number | null;
    leasing_team_role: string;
}

// a team is assigned when the user row is created (from the onboarding invite),
// so this is always available on the user object, including mid-onboarding
export interface ITeam {
    id: number;
    name: string;
    is_active: boolean;
    // (refers to whether this account is for testing/demo purposes)
    is_testing: boolean;
    // the organization this team belongs to, if any
    org_id: number | null;
    // timestamp in ISO format (if no timezone, assume UTC)
    created_at: string;
}

/**
 * This should be consistent with User.to_json() in the backend
 */
export interface IUser {
    id: number;

    uuid: string;

    /**
     * Guaranteed to be set
     */
    email: string;
    /**
     * Guaranteed to be set
     */
    name: string;

    profile_photo_url?: string | null;

    phone_number: string | null;
    // only relevant for landlord
    is_onboarding_complete: boolean;

    created_at: Date;
    modified_at: Date;

    // this is only set for landlords
    lead_contact?: ILeadContact;

    /**
     * This field is guaranteed to exist for landlords when onboarding is complete
     * They will be set after the /asssociate-email endpoint is called
     */
    ingest_emails?: IIngestEmail;

    notification_preferences: INotificationPreferences;

    landlord_onboard_invite?: ILandlordOnboardInvite;

    /**
     * Assigned when the landlord user is created from the onboarding invite,
     * so it is always set — including before onboarding is complete
     */
    leasing_team_id: number;

    /**
     * Should be set if onboarding is complete
     */
    leasing_team_role?: LeasingTeamRole;

    last_cleanup_at: Date | null;

    is_active: boolean;

    /**
     * The team matching leasing_team_id — assigned at user creation, so it is
     * always present, including during onboarding
     */
    team: ITeam;

    org_user_id: number | null;

    /**
     * Nested presence object (shift-support feature). Only present when the team
     * availability feature is enabled; undefined while loading or when off.
     */
    presence?: IPresenceInfo | null;

    /**
     * Per-day working hours schedule. 7 entries indexed Monday=0 … Sunday=6.
     * null means no schedule configured (user appears online by default).
     */
    working_hours_schedule?: Array<{
        enabled: boolean;
        start?: string;
        end?: string;
    }> | null;

    settings: UserSettings;
}

export const isUserAway = (user?: Pick<IUser, 'presence'>): boolean =>
    user?.presence?.value === EPresence.AWAY;

export const isManualAway = (user?: Pick<IUser, 'presence'>): boolean =>
    user?.presence?.value === EPresence.AWAY && user?.presence?.source === EPresenceSource.OVERRIDE;

export const isManualOnline = (user?: Pick<IUser, 'presence'>): boolean =>
    user?.presence?.value === EPresence.ONLINE && user?.presence?.source === EPresenceSource.OVERRIDE;

export const needsWorkingHoursSetup = (user?: Pick<IUser, 'presence'>): boolean =>
    user?.presence?.source === EPresenceSource.DEFAULT;

export type IOrgUserData = Pick<IUser, 'name' | 'leasing_team_id' | 'name' | 'uuid' | 'leasing_team_role'>

export interface IOrgUser {
    id: number;
    org_id: number;
    role: string;
    users: IOrgUserData[]
}

export function isCompanyAdmin(user: IUser): boolean {
    return user.leasing_team_role === LeasingTeamRole.COMPANY_ADMIN;
}
